// /finance/loan — the home loan from settlement to payoff (model/finance/
// loans.js + loan-engine.js): summary tiles, a note when a statement differed
// from the model, the owing / offset chart (finance-loan-chart.js, lazily
// imported), the loans (portions, refinancing), contribution plans and the
// history of entries. Dialogs: finance-loan-editors.js.
import { initNav } from '../components/nav.js';
import { initAuthUI } from '../auth/auth-ui.js';
import { buildDocClient, fetchIdentityId } from '../core/aws.js';
import { createCard } from '../components/card.js';
import { createDetailList } from '../components/detail-list.js';
import { createKebabMenu } from '../components/kebab-menu.js';
import { showToast, flushToasts } from '../components/toast.js';
import { stateEmpty, stateLoading } from '../components/empty-state.js';
import { setInfoBox } from '../components/info-box.js';
import { isoDate } from '../model/finance/fy.js';
import { listEmployments } from '../model/finance/income.js';
import { shortDate } from '../model/finance/income-view.js';
import { deleteLoan, deleteLoanItem, loadLoanData } from '../model/finance/loans.js';
import { simulateLoans } from '../model/finance/loan-engine.js';
import {
  describeContributionPlan,
  describeLoan,
  driftNote,
  historyItems,
  loanChartData,
  loanTiles,
} from '../model/finance/loan-view.js';
import { chartFigure, planButton, renderTiles } from './finance-view.js';
import {
  createContributionEditor,
  createEntryEditor,
  createLoanEditor,
  createPeopleEditor,
} from './finance-loan-editors.js';

const state = {
  doc: null,
  userId: null,
  loans: [],
  events: [],
  offset: [],
  contributions: [],
  people: [],
  employments: [],
  loaded: false,
};

let loanEditor = null;
let entryEditor = null;
let planEditor = null;
let peopleEditor = null;

const els = {
  actions: document.querySelector('#loan-actions'),
  error: document.querySelector('#loan-error'),
  drift: document.querySelector('#loan-drift'),
  empty: document.querySelector('#loan-empty'),
  data: [...document.querySelectorAll('.loan-data')],
  summary: document.querySelector('#loan-summary'),
  chart: document.querySelector('#loan-chart'),
  loans: document.querySelector('#loan-loans'),
  contributions: document.querySelector('#loan-contributions'),
  history: document.querySelector('#loan-history'),
  addEntry: document.querySelector('#loan-add-entry'),
};

const chart = chartFigure('What you owe and the offset balance over time, projected past today');
let chartModule = null;

async function main() {
  initNav();
  await initAuthUI(handleSignedIn);
}

main().catch((err) => console.error('Page bootstrap failed:', err));

async function handleSignedIn(session) {
  const { idToken } = session;
  state.doc = await buildDocClient(idToken);
  state.userId = await fetchIdentityId(idToken);
  const getState = () => state;
  loanEditor = createLoanEditor({ getState, onSaved: refresh });
  entryEditor = createEntryEditor({ getState, onSaved: refresh });
  planEditor = createContributionEditor({ getState, onSaved: refresh });
  peopleEditor = createPeopleEditor({ getState, onSaved: refresh });
  els.addEntry.addEventListener('click', () => entryEditor.open(null));
  renderActions();
  await refresh();
}

async function refresh() {
  await flushToasts();
  if (!state.loaded) {
    for (const el of els.data) el.hidden = true;
    els.empty.replaceChildren(stateLoading('home loan'));
  }
  try {
    const [data, employments] = await Promise.all([
      loadLoanData(state.doc, state.userId),
      listEmployments(state.doc, state.userId),
    ]);
    Object.assign(state, data, { employments, loaded: true });
    setInfoBox(els.error, null);
  } catch (err) {
    console.error('Loading the home loan failed:', err);
    setInfoBox(els.error, 'error', 'Could not load your home loan — please reload the page.');
    els.empty.replaceChildren();
    return;
  }
  render();
}

function derive() {
  const today = isoDate(new Date());
  const sim = simulateLoans({
    loans: state.loans,
    events: state.events,
    offset: state.offset,
    contributions: state.contributions,
    employments: state.employments,
    today,
  });
  return { today, sim };
}

// ─── Render ──────────────────────────────────────────────────────────────────

function render() {
  const d = derive();
  const empty = !state.loans.length;
  els.addEntry.hidden = empty;
  for (const el of els.data) el.hidden = empty;
  els.empty.replaceChildren(
    ...(empty
      ? [stateEmpty('No home loan yet — choose “Add a loan” in the ⋮ menu to start.')]
      : []),
  );
  const note = empty ? null : driftNote(d.sim);
  setInfoBox(els.drift, note?.kind ?? null, note?.message ?? null);
  renderActions();
  if (empty) return;
  els.summary.replaceChildren(renderTiles(loanTiles(d.sim)));
  renderChart(d);
  renderLoans(d);
  renderContributions(d);
  renderHistory();
}

function contributionsAction() {
  const latest = state.contributions.at(-1) ?? null;
  return {
    label: latest ? 'Change contributions' : 'Add contributions',
    open: () => planEditor.open(null, { basedOn: latest }),
  };
}

function renderActions() {
  els.actions.replaceChildren(
    createKebabMenu({
      label: 'Home loan actions',
      items: [
        { label: 'Add a loan', icon: 'fa-house', onSelect: () => loanEditor.open(null) },
        ...(state.loans.length
          ? [{ label: 'Household people', icon: 'fa-users', onSelect: () => peopleEditor.open() }]
          : []),
      ],
    }),
  );
}

async function renderChart(d) {
  if (!els.chart.contains(chart.figure)) els.chart.append(chart.figure);
  const data = loanChartData(d.sim, d.today);
  chart.figure.hidden = data.labels.length < 2;
  if (chart.figure.hidden) return;
  try {
    chartModule ??= import('./finance-loan-chart.js');
    const { createLoanChart } = await chartModule;
    chart.handle ??= createLoanChart(chart.canvas);
    chart.handle.update(data);
  } catch (err) {
    console.error('Failed to draw the home loan chart:', err);
    chart.figure.hidden = true;
  }
}

function list(cards, className) {
  const ul = document.createElement('ul');
  ul.className = `card-list ${className}`;
  ul.append(...cards);
  return ul;
}

function renderLoans(d) {
  const cards = [...state.loans].reverse().map((loan) => {
    const { title, badges, details } = describeLoan(loan, {
      events: state.events,
      loans: state.loans,
      today: d.today,
    });
    const open = !loan.closedOn;
    return createCard({
      control: { icon: 'fa-house' },
      title,
      badges,
      content: createDetailList(details),
      dataset: { itemId: loan.itemId },
      actions: [
        {
          icon: 'fa-pen',
          title: 'Edit',
          ariaLabel: `Edit ${loan.name}`,
          onClick: () => loanEditor.open(loan),
        },
        ...(open
          ? [
              {
                icon: 'fa-right-left',
                title: 'Refinance',
                ariaLabel: `Refinance ${loan.name}`,
                kebab: true,
                onClick: () =>
                  loanEditor.open(null, {
                    refinanceOf: loan,
                    owingCents: d.sim?.now?.balanceCents ?? null,
                  }),
              },
            ]
          : []),
        {
          icon: 'fa-trash',
          title: 'Delete',
          ariaLabel: `Delete ${loan.name}`,
          danger: true,
          kebab: true,
          onClick: () => handleDeleteLoan(loan),
        },
      ],
    });
  });
  els.loans.replaceChildren(list(cards, 'loan-list'));
}

/** "Contributions": an add/change button, then a card per plan (newest first). */
function renderContributions(d) {
  const plans = state.contributions;
  const add = planButton(contributionsAction(), 'loan-add-plan');
  if (!plans.length) {
    const p = document.createElement('p');
    p.className = 'muted';
    p.textContent =
      'None yet. Add what each person pays into the offset account to project it forward.';
    els.contributions.replaceChildren(add, p);
    return;
  }
  const currentIndex = plans.findLastIndex((p) => p.from <= d.today);
  const cards = plans
    .map((plan, i) => {
      const { title, badges, details } = describeContributionPlan(plan, state.people, {
        current: i === currentIndex,
      });
      return createCard({
        control: { icon: 'fa-calendar-plus' },
        title,
        badges,
        content: createDetailList(details),
        dataset: { itemId: plan.itemId },
        actions: [
          {
            icon: 'fa-pen',
            title: 'Edit',
            ariaLabel: `Edit contributions from ${shortDate(plan.from)}`,
            onClick: () => planEditor.open(plan),
          },
          {
            icon: 'fa-trash',
            title: 'Delete',
            ariaLabel: `Delete contributions from ${shortDate(plan.from)}`,
            danger: true,
            onClick: () =>
              handleDelete(plan, 'contributions', `the contributions from ${shortDate(plan.from)}`),
          },
        ],
      });
    })
    .reverse();
  els.contributions.replaceChildren(add, list(cards, 'loan-plan-list'));
}

function renderHistory() {
  const items = historyItems(state);
  if (!items.length) {
    els.history.replaceChildren(
      stateEmpty(
        'No entries yet — use + to record rate changes, extra repayments and statement balances.',
      ),
    );
    return;
  }
  const cards = items.map((item) =>
    createCard({
      control: { icon: item.icon },
      title: item.title,
      titleStyle: 'plain',
      meta: item.meta,
      dataset: { itemId: item.itemId },
      actions: [
        {
          icon: 'fa-pen',
          title: 'Edit',
          ariaLabel: `Edit ${item.title}`,
          onClick: () => entryEditor.open(item),
        },
        {
          icon: 'fa-trash',
          title: 'Delete',
          ariaLabel: `Delete ${item.title}`,
          danger: true,
          onClick: () =>
            handleDelete(
              item.row,
              item.source === 'offset' ? 'offset' : 'events',
              `${item.title.charAt(0).toLowerCase()}${item.title.slice(1)} on ${shortDate(item.date)}`,
            ),
        },
      ],
    }),
  );
  els.history.replaceChildren(list(cards, 'loan-history-list'));
}

// ─── Deletes ─────────────────────────────────────────────────────────────────

/** Remove a row of `state[key]` now (optimistically) with an Undo toast. */
function handleDelete(row, key, what) {
  const before = state[key];
  state[key] = before.filter((r) => r.itemId !== row.itemId);
  render();
  const restore = () => {
    if (!state[key].some((r) => r.itemId === row.itemId)) {
      state[key] = [...state[key], row].sort((a, b) =>
        (a.date ?? a.from).localeCompare(b.date ?? b.from),
      );
    }
    render();
  };
  showToast({
    message: `Removed ${what}`,
    onUndo: restore,
    onCommit: async () => {
      try {
        await deleteLoanItem(state.doc, state.userId, row.itemId);
      } catch (err) {
        console.error('Delete failed:', err);
        restore();
        setInfoBox(els.error, 'error', 'Could not delete — please try again.');
      }
    },
  });
}

/** Remove a loan (and its entries) with an Undo toast. */
function handleDeleteLoan(loan) {
  const saved = { loans: state.loans, events: state.events };
  state.loans = state.loans.filter((l) => l.itemId !== loan.itemId);
  state.events = state.events.filter((e) => e.loanRef !== loan.itemId);
  render();
  const restore = () => {
    Object.assign(state, saved);
    render();
  };
  showToast({
    message: `Removed ${loan.name} and its entries`,
    onUndo: restore,
    onCommit: async () => {
      try {
        await deleteLoan(state.doc, state.userId, loan.itemId);
      } catch (err) {
        console.error('Delete failed:', err);
        restore();
        setInfoBox(els.error, 'error', 'Could not delete — please try again.');
      }
    },
  });
}
